import { useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DefinitionTooltip } from "src/components/common";
import type { PaidMediaOverviewPlatform } from "src/services/PaidMediaOverviewAPI";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { getOverviewTooltip } from "../../utils/paidMediaOverviewTooltips";
import { useOverviewAttribution } from "../../utils/paidMediaOverviewAttributionContext";
import {
  overviewStatusLabel,
  overviewStatusModifier,
} from "../../utils/paidMediaOverviewLabels";
import { formatOverviewPct, OVERVIEW_EMPTY_VALUE } from "../../utils/paidMediaOverviewFormat";
import {
  overviewPlatformDescriptor,
  type OverviewPlatformSlug,
} from "../../data/paidMediaOverviewPlatforms";

export function OverviewSegmented<T extends string>({
  value,
  onChange,
  options,
  size = "md",
  ariaLabel,
}: {
  value: T;
  onChange: (_value: T) => void;
  options: Array<{
    value: T;
    label: string;
    disabled?: boolean;
    tooltip?: { title?: string; description: string };
  }>;
  size?: "sm" | "md";
  ariaLabel?: string;
}) {
  const [hoveredValue, setHoveredValue] = useState<T | null>(null);

  return (
    <fieldset
      className={`paid-media-overview__segmented paid-media-overview__segmented--${size}`}
    >
      {ariaLabel ? <legend className="paid-media-visually-hidden">{ariaLabel}</legend> : null}
      {options?.map((option, index) => {
        const showTooltip = hoveredValue === option.value;
        const selected = value === option.value;
        const isLastOption = index === (options?.length ?? 0) - 1;

        return (
          <div
            key={option.value}
            className="paid-media-overview__segmented-option"
            onPointerEnter={() => setHoveredValue(option.value)}
            onPointerLeave={() => setHoveredValue(null)}
            onFocus={() => setHoveredValue(option.value)}
            onBlur={() => setHoveredValue(null)}
            onTouchStart={() => setHoveredValue(option.value)}
            onTouchEnd={() => setHoveredValue(null)}
          >
            <button
              type="button"
              onClick={() => onChange(option.value)}
              aria-pressed={selected}
              disabled={option.disabled}
              className={
                selected
                  ? "paid-media-overview__segmented-btn paid-media-overview__segmented-btn--active"
                  : "paid-media-overview__segmented-btn"
              }
            >
              {option.label}
            </button>
            {option.tooltip ? (
              <div
                className={`paid-media-overview__segmented-tooltip ${
                  isLastOption ? "paid-media-overview__segmented-tooltip--end" : ""
                } ${showTooltip ? "is-visible" : ""}`}
                aria-hidden={!showTooltip}
              >
                <div className="paid-media-overview__segmented-tooltip-card">
                  <div className="paid-media-overview__segmented-tooltip-title">
                    {option.tooltip.title ?? option.label}
                  </div>
                  <div className="paid-media-overview__segmented-tooltip-description">
                    {option.tooltip.description}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </fieldset>
  );
}

export function OverviewChartEmptyState({ message }: { message?: string }) {
  const { t } = useTranslation();
  return (
    <div className="paid-media-overview__chart-empty" role="status">
      {message ?? t("paidMediaOverview.emptyChart")}
    </div>
  );
}

export function OverviewChartLoadingState() {
  return (
    <div className="paid-media-overview__chart-empty" role="status" aria-live="polite">
      <LoadingSpinner compact />
    </div>
  );
}

export function OverviewMetricTip({ id, label, formulaTitle }: { id: string; label: string; formulaTitle?: string }) {
  const { t } = useTranslation();
  const mode = useOverviewAttribution();
  return (
    <DefinitionTooltip
      label={label}
      info={getOverviewTooltip(t, id, mode)}
      formulaTitle={formulaTitle}
    />
  );
}

export type OverviewShareBarVariant =
  | "meta-ads"
  | "google-ads"
  | "tiktok-ads"
  | "snapchat-ads"
  | "confidence-green"
  | "confidence-blue"
  | "confidence-purple"
  | "confidence-orange";

export function OverviewShareBar({
  label,
  value,
  variant,
  right,
  tooltipId,
}: {
  label: string;
  value: number | null;
  variant: OverviewShareBarVariant;
  right?: ReactNode;
  tooltipId?: string;
}) {
  const hasValue = value !== null && Number.isFinite(value);
  const clamped = hasValue ? Math.max(0, Math.min(100, value as number)) : 0;

  return (
    <div className="paid-media-overview__share-bar">
      <div className="paid-media-overview__share-bar-row">
        <span className="paid-media-overview__share-bar-label">
          {label}
          {tooltipId ? <OverviewMetricTip id={tooltipId} label={label} /> : null}
        </span>

        <span className="paid-media-overview__share-bar-value">
          {right ?? formatOverviewPct(value, 0)}
        </span>
      </div>

      <div
        className="paid-media-overview__share-bar-track"
        role="meter"
        aria-label={label}
        aria-valuenow={hasValue ? clamped : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
      >

        <div
          className={`paid-media-overview__share-bar-fill paid-media-overview__share-bar-fill--${variant}`}
          style={{ "--paid-media-share-bar-value": `${clamped}%` } as CSSProperties}
        />
      </div>
    </div>
  );
}

export function OverviewStatusBadge({
  code,
  label,
  reason,
}: {
  code: string | null;
  label: string | null;
  reason: string | null;
}) {
  const { t } = useTranslation();
  const displayLabel = overviewStatusLabel(t, code, label);

  if (!displayLabel) {
    return <span className="paid-media-overview__status-badge">{OVERVIEW_EMPTY_VALUE}</span>;
  }

  return (
    <span
      className={`paid-media-overview__status-badge paid-media-overview__status-badge--${overviewStatusModifier(
        code
      )}`}
    >
      {displayLabel}
      {reason ? <DefinitionTooltip label={displayLabel} info={{ description: reason }} /> : null}
    </span>
  );
}

export function OverviewPlatformDot({
  platform,
}: {
  platform: PaidMediaOverviewPlatform | null | undefined;
}) {
  const { t } = useTranslation();
  const descriptor = overviewPlatformDescriptor(platform);
  const slug: OverviewPlatformSlug | "unknown" = descriptor?.slug ?? "unknown";

  return (
    <span className="paid-media-overview__platform-dot">
      <span
        className={`paid-media-overview__platform-dot-swatch paid-media-overview__platform-dot-swatch--${slug}`}
        aria-hidden
      />
      {descriptor ? t(descriptor.labelKey) : OVERVIEW_EMPTY_VALUE}
    </span>
  );
}
